/**
 * One-retry helper for Supabase calls that hit transient TLS / socket
 * resets between Vercel and the Supabase edge.
 *
 * Why this exists: Vercel's serverless containers occasionally tear down
 * their idle outbound TLS connection to `*.supabase.co` during cold-start
 * recycle windows. The next call hits an `ECONNRESET` (`UND_ERR_SOCKET`
 * / `other side closed`) or `ETIMEDOUT`. These are rare but real, and
 * because Supabase JS's `auth.getUser()` resolves with
 * `{ data: { user: null }, error }` on network failure (rather than
 * throwing), the proxy was silently treating those cases as "logged
 * out" and bouncing live sessions to /auth.
 *
 * Retries once after a small delay. Anything beyond that is a real
 * outage — the second-attempt failure should propagate.
 *
 * Two shapes are supported:
 *
 *   - `retryOnSupabaseNetworkError(thunk)` for `await supabase.from(...).select()`
 *     style calls that resolve with `{ data, error }` on failure.
 *   - `retryOnNetworkThrow(thunk)` for plain async calls that throw on
 *     network failure (rare in supabase-js, but useful for `fetch()`
 *     wrappers).
 */

const RETRY_DELAY_MS = 150

// Light incremental backoff for the fetch-layer retry (retryFetch). Two
// retries (three total attempts) — a single retry still left a steady drip of
// `AuthRetryableFetchError` in the Vercel logs when both attempts landed on the
// same transient Vercel→Supabase socket reset. The extra attempt with a longer
// second delay clears almost all of them without meaningfully adding latency on
// the happy path (which returns on attempt #1 and never sleeps).
const RETRY_FETCH_DELAYS_MS = [150, 400] as const

const NETWORK_ERROR_HINTS = [
  'fetch failed',
  'Client network socket disconnected',
  'other side closed',
  'ECONNRESET',
  'ETIMEDOUT',
  'UND_ERR_SOCKET',
  'AuthRetryableFetchError',
  // Sanity's get-it HTTP layer surfaces its own socket timeout as these (the
  // dominant ISR-revalidation error). They were NOT matched before, so the
  // app-level sfetch retry (lib/sanity/cached-content.ts) silently no-op'd on
  // exactly the error it exists to catch. NB: these are distinct from an
  // AbortSignal `TimeoutError` — we deliberately do NOT list that, so the proxy
  // client's 20s outer abort stays non-retryable (see fetchWithRetry).
  'ESOCKETTIMEDOUT',
  'Socket timed out',
] as const

/** Returns true when the supplied error looks like a transient network reset. */
export function isTransientNetworkError(err: unknown): boolean {
  if (!err) return false
  // Supabase auth errors carry the wrapped cause on `.cause` or surface
  // the hint string on `.message` / `.name` (varies by version).
  // Walk the chain a few levels to catch all of them.
  const seen = new Set<unknown>()
  let cur: unknown = err
  for (let depth = 0; depth < 5 && cur && !seen.has(cur); depth++) {
    seen.add(cur)
    const obj = cur as { name?: string; message?: string; code?: string; cause?: unknown }
    const haystack = `${obj.name ?? ''} ${obj.message ?? ''} ${obj.code ?? ''}`
    if (NETWORK_ERROR_HINTS.some((hint) => haystack.includes(hint))) {
      return true
    }
    cur = obj.cause
  }
  return false
}

/**
 * Retries a supabase-js style call once on transient network error.
 * The thunk should return `{ data, error }` (or any object with an
 * `.error` field). Returns the second-attempt result whether success or
 * failure — caller still has to handle the error case.
 *
 * Accepts both `Promise<T>` and `PromiseLike<T>` so PostgrestBuilder
 * chains (`.from(...).select(...).single()`) — which are thenable but
 * not strictly Promises — pass through without forcing callers to add
 * an explicit `Promise.resolve()` wrap.
 *
 * Generic is unconstrained so call sites that already use `as any`
 * casts (legacy untyped routes) preserve their inferred shape rather
 * than collapsing to `{ error: unknown }`.
 */
export async function retryOnSupabaseNetworkError<T>(
  thunk: () => Promise<T> | PromiseLike<T>,
): Promise<T> {
  const first = await thunk()
  const err = (first as { error?: unknown } | null | undefined)?.error
  if (!err) return first
  if (!isTransientNetworkError(err)) return first
  await new Promise((r) => setTimeout(r, RETRY_DELAY_MS))
  return thunk()
}

/**
 * Retries an arbitrary async thunk on transient network throw. Use for callers
 * that throw rather than return `{ error }`.
 *
 * `retries` is the number of RETRIES after the first attempt (default 1, so one
 * retry — unchanged for existing callers). The backoff before each retry is
 * 150ms then 400ms, matching `retryFetch`, so both resilience layers behave the
 * same. A non-transient error, or exhausting the retries, rethrows — a genuine
 * outage still propagates rather than being masked.
 */
export async function retryOnNetworkThrow<T>(
  thunk: () => Promise<T>,
  retries = 1,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await thunk()
    } catch (err) {
      if (!isTransientNetworkError(err) || attempt >= retries) throw err
      await new Promise((r) => setTimeout(r, attempt === 0 ? RETRY_DELAY_MS : 400))
    }
  }
}

/**
 * Fetch wrapper that retries transient network errors (up to two retries,
 * light backoff — see RETRY_FETCH_DELAYS_MS).
 *
 * Pass as `global.fetch` to `createServerClient` / `createBrowserClient`
 * so every supabase call (auth, postgrest, storage, realtime) inherits
 * resilience without per-callsite plumbing. The same Vercel→Supabase
 * cold-start TLS resets that this module's other helpers catch at the
 * "Supabase result" layer get caught here at the HTTP layer first —
 * which has the bonus effect of suppressing the supabase-js internal
 * retry logging (`AuthRetryableFetchError: fetch failed` in Vercel
 * logs) because supabase-js never sees the failure when our retry
 * succeeds on a later attempt.
 *
 * Retries idempotent methods (GET/HEAD/PUT) AND the GoTrue token
 * endpoint POST (login / session refresh — see isRetryableTokenPost).
 * Other POST/PATCH/DELETE fall through on the first failure: retrying a
 * non-idempotent PostgREST write that may have already been processed
 * server-side can produce duplicates (e.g. double-insert), which is the
 * more expensive failure mode than a one-time log line.
 */
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'PUT'])

/** Normalize the fetch `input` union (string | URL | Request) to a URL string. */
function fetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

/**
 * The GoTrue token endpoint (`/auth/v1/token` — password login and, the case
 * that dominates our log noise, `grant_type=refresh_token` session refresh as a
 * token crosses its hourly expiry). Safe to retry HERE specifically: retryFetch
 * only retries when `fetch()` THREW, i.e. no HTTP response was received, so
 * GoTrue never processed the request and the refresh token was not consumed. A
 * retry that races a server-side success just fails cleanly ("token already
 * used") and supabase-js falls back — strictly better than the guaranteed error
 * log we get today. We opt in this one path without loosening the double-write
 * protection above for PostgREST POSTs.
 */
function isRetryableTokenPost(method: string, url: string): boolean {
  return method === 'POST' && url.includes('/auth/v1/token')
}

/** Outcome reported to {@link RetryFetchOptions.onSettle}. */
export type RetryFetchOutcome =
  | 'ok'
  | 'per-attempt-timeout'
  | 'caller-abort'
  | 'error'

export interface RetryFetchOptions {
  /**
   * Per-attempt deadline (ms) for a RETRYABLE request. When set, each attempt
   * gets its OWN `AbortSignal.timeout`; if that fires while the caller's own
   * signal has NOT, the attempt is retried on a FRESH socket. This converts an
   * upstream "connected but no bytes return" stall from a single long dead-wait
   * (bounded only by the caller's outer signal, if any) into a fast-fail +
   * reconnect — which is what a socket-specific stall actually needs.
   *
   * The caller's own `signal` stays the NON-retryable outer backstop: when IT
   * aborts we stop and throw (so e.g. the proxy's 20s cap is preserved and is
   * NOT made retryable — that would double worst-case latency).
   *
   * Deliberately opt-in: only the request-path (proxy) client sets it. The
   * shared server/admin clients must keep long-running reads (migrations, bulk
   * ops, large PostgREST selects) un-clipped, so plain `retryFetch` never
   * applies a per-attempt deadline.
   */
  perAttemptTimeoutMs?: number
  /** Fired once when the call finally settles (success or terminal throw).
   * `status` is the HTTP status on a returned response (absent on a throw), so
   * observers can treat a persistent 5xx/slow-200 as noteworthy even though the
   * fetch itself did not throw. */
  onSettle?: (info: {
    attempts: number
    outcome: RetryFetchOutcome
    ms: number
    status?: number
  }) => void
}

/**
 * Core retrying fetch. `retryFetch` is the plain (no per-attempt deadline)
 * form installed as `global.fetch` on every supabase client; the proxy client
 * calls this directly with `perAttemptTimeoutMs` set — see lib/supabase/proxy.ts.
 */
export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  opts: RetryFetchOptions = {},
): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const retryable =
    IDEMPOTENT_METHODS.has(method) || isRetryableTokenPost(method, fetchUrl(input))
  const callerSignal = init?.signal ?? undefined
  const { perAttemptTimeoutMs, onSettle } = opts
  // The per-attempt deadline is applied ONLY to idempotent methods — NEVER the
  // GoTrue token-refresh POST: re-sending a refresh token after a TTFB abort
  // (when GoTrue may already have rotated it) risks reuse-detection revoking the
  // whole session. The POST keeps its throw-only retry on the caller's deadline.
  const perAttemptEligible =
    IDEMPOTENT_METHODS.has(method) &&
    typeof perAttemptTimeoutMs === 'number' &&
    perAttemptTimeoutMs > 0
  const startedAt = Date.now()

  for (let attempt = 0; ; attempt++) {
    // Clip only the FIRST attempt. A per-socket stall is cleared by one
    // fast-fail + fresh-socket reconnect; every LATER attempt runs UN-clipped
    // against the caller's own deadline (e.g. the proxy 20s cap), so a
    // backend-wide slow spell where a healthy call takes ~10s still SUCCEEDS on
    // the unclipped retry instead of being clipped at every attempt and failing
    // — the 2026-10-01 regression class this must avoid.
    const applyPerAttempt = perAttemptEligible && attempt === 0
    let timeoutSignal: AbortSignal | undefined
    let signal = callerSignal
    if (applyPerAttempt) {
      timeoutSignal = AbortSignal.timeout(perAttemptTimeoutMs!)
      signal = callerSignal
        ? AbortSignal.any([callerSignal, timeoutSignal])
        : timeoutSignal
    }
    try {
      const res = await fetch(input, signal ? { ...init, signal } : init)
      onSettle?.({
        attempts: attempt + 1,
        outcome: 'ok',
        ms: Date.now() - startedAt,
        status: res.status,
      })
      return res
    } catch (err) {
      // The caller's own signal (e.g. the proxy 20s cap) is the hard stop — if
      // IT aborted, never retry. Our per-attempt deadline, in contrast, IS a
      // retry trigger: a fresh socket often clears a connection-specific stall.
      const callerAborted = !!callerSignal?.aborted
      const perAttemptFired = !!timeoutSignal?.aborted && !callerAborted
      const canRetry =
        retryable &&
        !callerAborted &&
        attempt < RETRY_FETCH_DELAYS_MS.length &&
        (isTransientNetworkError(err) || perAttemptFired)
      if (!canRetry) {
        onSettle?.({
          attempts: attempt + 1,
          outcome: callerAborted
            ? 'caller-abort'
            : perAttemptFired
              ? 'per-attempt-timeout'
              : 'error',
          ms: Date.now() - startedAt,
        })
        throw err
      }
      await new Promise((r) => setTimeout(r, RETRY_FETCH_DELAYS_MS[attempt]))
    }
  }
}

export const retryFetch: typeof fetch = (input, init) => fetchWithRetry(input, init)
